import React, { useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { useHistory } from 'react-router-dom'
import { DSText, DSSpacer } from '@zoe/ds-web'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { onNextClickType } from '../types'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const Ring = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 6px solid #ede8de;
  border-top-color: #ffd100;
  animation: ${spin} 1.2s linear infinite;
  margin: 0 auto;
`

const CalculatingCard = styled.div`
  background: var(--colour-surface-cream-subtle);
  border-radius: 16px;
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

// Skeleton shimmer: a cream-bold band sweeps across the cream-subtle base while
// the score is being calculated.
const shimmer = keyframes`
  0% { background-position: 150% 0; }
  100% { background-position: -150% 0; }
`

const GhostCard = styled.div`
  border-radius: 16px;
  height: 64px;
  width: 100%;
  background: linear-gradient(
    90deg,
    var(--colour-surface-cream-subtle) 25%,
    var(--colour-surface-cream-bold) 50%,
    var(--colour-surface-cream-subtle) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s linear infinite;
`

export type SymptomScoreCalculatingScreenProps = {
  durationMs?: number
}

type Props = SymptomScoreCalculatingScreenProps & {
  onNextClick: onNextClickType
}

// Set by SymptomScoreResultsScreen on mount and consumed here so we only
// step further back when the user is actually navigating back from the
// results screen (not when they refresh the page on the calculator — react
// router reports action === 'POP' on every initial load).
export const VISITED_SYMPTOM_RESULTS_KEY = 'visited_symptom_results'

export const SymptomScoreCalculatingScreen: React.FC<Props> = ({ durationMs = 3500, onNextClick }) => {
  const history = useHistory()
  useEffect(() => {
    // If the user landed here via back-navigation from the results screen, the
    // browser history is …, last-slider, calculating, score. Auto-advancing
    // would push them straight back to score and trap them in a loop. Step
    // further back instead — but only when we know the results screen really
    // was visited; otherwise (e.g. hard refresh) honour the normal forward
    // auto-advance.
    const cameBackFromResults =
      history.action === 'POP' && sessionStorage.getItem(VISITED_SYMPTOM_RESULTS_KEY) === 'true'
    if (cameBackFromResults) {
      sessionStorage.removeItem(VISITED_SYMPTOM_RESULTS_KEY)
      history.goBack()
      return
    }
    const t = setTimeout(() => (onNextClick as () => void)(), durationMs)
    return () => clearTimeout(t)
  }, [durationMs, onNextClick, history])

  return (
    <ScreenContainer>
      <CalculatingCard>
        <DSText variant="fluid-paragraph-300" weight="regular">
          We&rsquo;re calculating your score..
        </DSText>
        <DSSpacer size={24} direction="vertical" />
        <Ring />
      </CalculatingCard>
      <DSSpacer size={32} direction="vertical" />
      <DSText variant="fluid-heading-500" weight="semiBold">
        Your main symptoms
      </DSText>
      <DSSpacer size={16} direction="vertical" />
      <GhostCard />
      <DSSpacer size={12} direction="vertical" />
      <GhostCard />
      <DSSpacer size={12} direction="vertical" />
      <GhostCard />
    </ScreenContainer>
  )
}
